import type { CommissionRuleInput } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export const COMMISSIONS_ROUTES = {
  RULES: '/comissoes/regras',
  REPORT: '/comissoes',
} as const;

const ENDPOINT = '/api/v1/commissions';

export interface CommissionRule {
  id: string;
  companyId: string;
  professionalId: string | null;
  professionalName: string | null;
  serviceId: string | null;
  serviceName: string | null;
  percent: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CommissionReport {
  from: string;
  to: string;
  totals: { services: number; revenue: number; commission: number };
  byProfessional: {
    professionalId: string | null;
    professionalName: string;
    services: number;
    revenue: number;
    commission: number;
  }[];
}

export interface CommissionReportQuery {
  from: string;
  to: string;
  professionalId?: string;
  unitId?: string;
}

export const commissionsApi = {
  listRules: () => apiClient.get<{ data: CommissionRule[] }>(`${ENDPOINT}/rules`),
  createRule: (input: CommissionRuleInput) =>
    apiClient.post<CommissionRule>(`${ENDPOINT}/rules`, input),
  updateRule: (id: string, input: Partial<CommissionRuleInput>) =>
    apiClient.patch<CommissionRule>(`${ENDPOINT}/rules/${id}`, input),
  removeRule: (id: string) => apiClient.delete<{ message: string }>(`${ENDPOINT}/rules/${id}`),

  report(params: CommissionReportQuery) {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.professionalId) query.set('professionalId', params.professionalId);
    if (params.unitId) query.set('unitId', params.unitId);

    return apiClient.get<CommissionReport>(`${ENDPOINT}/report?${query}`);
  },
};

const KEYS = { rules: ['commission-rules'] as const, report: ['commission-report'] as const };

export function useCommissionRules() {
  return useQuery({ queryKey: KEYS.rules, queryFn: () => commissionsApi.listRules() });
}

export function useCommissionReport(params: CommissionReportQuery) {
  return useQuery({
    queryKey: [...KEYS.report, params],
    queryFn: () => commissionsApi.report(params),
    placeholderData: (previous) => previous,
  });
}

function useRuleMutation<TArgs>(mutationFn: (args: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.rules });
      // A apuracao muda junto: a regra nova vale para o periodo aberto na tela.
      queryClient.invalidateQueries({ queryKey: KEYS.report });
    },
  });
}

export function useCreateCommissionRule() {
  return useRuleMutation((input: CommissionRuleInput) => commissionsApi.createRule(input));
}

export function useUpdateCommissionRule() {
  return useRuleMutation(({ id, input }: { id: string; input: Partial<CommissionRuleInput> }) =>
    commissionsApi.updateRule(id, input),
  );
}

export function useDeleteCommissionRule() {
  return useRuleMutation((id: string) => commissionsApi.removeRule(id));
}
